import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { cocktailApi, Cocktail } from '../services/cocktailApi.ts';
import CocktailCard from '../components/CocktailCard.tsx';
import { validateBrowseMode, validateBrowseValue, validateLetter } from '../utils/validation.ts';
import './Browse.css';

type BrowseMode = 'letter' | 'category' | 'glass' | 'alcoholic';

const Browse: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [browseMode, setBrowseMode] = useState<BrowseMode>('letter');
  const [selectedLetter, setSelectedLetter] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedGlass, setSelectedGlass] = useState<string>('');
  const [selectedAlcoholic, setSelectedAlcoholic] = useState<string>('');
  const [cocktails, setCocktails] = useState<Cocktail[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [glasses, setGlasses] = useState<string[]>([]);
  const [alcoholicTypes, setAlcoholicTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Helper function to sort cocktails alphabetically by drink name
  const sortCocktailsAlphabetically = (cocktails: Cocktail[]): Cocktail[] => {
    return [...cocktails].sort((a, b) => {
      const nameA = (a.strDrink || '').toLowerCase();
      const nameB = (b.strDrink || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  useEffect(() => {
    loadCategories();
    loadGlasses();
    loadAlcoholicTypes();
  }, []);

  // Load results from URL parameters
  useEffect(() => {
    const mode = validateBrowseMode(searchParams.get('mode'));
    const value = validateBrowseValue(searchParams.get('value'));

    if (mode && value) {
      // Additional validation for letter mode
      if (mode === 'letter') {
        const validatedLetter = validateLetter(value);
        if (validatedLetter) {
          setBrowseMode(mode);
          loadResultsFromParams(mode, validatedLetter);
        }
      } else {
        setBrowseMode(mode);
        loadResultsFromParams(mode, value);
      }
    }
  }, [searchParams]);

  const loadCategories = async () => {
    const cats = await cocktailApi.getCategories();
    setCategories(cats);
  };

  const loadGlasses = async () => {
    const glassTypes = await cocktailApi.getGlasses();
    setGlasses(glassTypes);
  };

  const loadAlcoholicTypes = async () => {
    const types = await cocktailApi.getAlcoholicTypes();
    setAlcoholicTypes(types);
  };

  const loadResultsFromParams = async (mode: BrowseMode, value: string) => {
    setLoading(true);
    let results: Cocktail[] = [];

    try {
      switch (mode) {
        case 'letter':
          setSelectedLetter(value);
          setSelectedCategory('');
          setSelectedGlass('');
          setSelectedAlcoholic('');
          results = await cocktailApi.getCocktailsByFirstLetter(value);
          break;
        case 'category':
          setSelectedCategory(value);
          setSelectedLetter('');
          setSelectedGlass('');
          setSelectedAlcoholic('');
          results = await cocktailApi.getCocktailsByCategory(value);
          break;
        case 'glass':
          setSelectedGlass(value);
          setSelectedLetter('');
          setSelectedCategory('');
          setSelectedAlcoholic('');
          results = await cocktailApi.getCocktailsByGlass(value);
          break;
        case 'alcoholic':
          setSelectedAlcoholic(value);
          setSelectedLetter('');
          setSelectedCategory('');
          setSelectedGlass('');
          results = await cocktailApi.getCocktailsByAlcoholic(value);
          break;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading browse results:', error);
      }
      results = [];
    }

    // Sort results alphabetically before setting
    setCocktails(sortCocktailsAlphabetically(results));
    setLoading(false);
  };

  const handleLetterClick = async (letter: string) => {
    setSelectedLetter(letter);
    setSelectedCategory(''); // Clear category selection
    setSelectedGlass('');
    setSelectedAlcoholic('');
    setLoading(true);
    const results = await cocktailApi.getCocktailsByFirstLetter(letter);
    setCocktails(sortCocktailsAlphabetically(results));
    setLoading(false);
    // Update URL
    navigate(`/browse?mode=letter&value=${letter}`, { replace: true });
  };

  const handleCategoryClick = async (category: string) => {
    setSelectedCategory(category);
    setSelectedLetter(''); // Clear other selections
    setSelectedGlass('');
    setSelectedAlcoholic('');
    setLoading(true);
    const results = await cocktailApi.getCocktailsByCategory(category);
    setCocktails(sortCocktailsAlphabetically(results));
    setLoading(false);
    // Update URL
    navigate(`/browse?mode=category&value=${encodeURIComponent(category)}`, { replace: true });
  };

  const handleGlassClick = async (glass: string) => {
    setSelectedGlass(glass);
    setSelectedLetter(''); // Clear other selections
    setSelectedCategory('');
    setSelectedAlcoholic('');
    setLoading(true);
    const results = await cocktailApi.getCocktailsByGlass(glass);
    setCocktails(sortCocktailsAlphabetically(results));
    setLoading(false);
    // Update URL
    navigate(`/browse?mode=glass&value=${encodeURIComponent(glass)}`, { replace: true });
  };

  const handleAlcoholicClick = async (alcoholic: string) => {
    setSelectedAlcoholic(alcoholic);
    setSelectedLetter(''); // Clear other selections
    setSelectedCategory('');
    setSelectedGlass('');
    setLoading(true);
    const results = await cocktailApi.getCocktailsByAlcoholic(alcoholic);
    setCocktails(sortCocktailsAlphabetically(results));
    setLoading(false);
    // Update URL
    navigate(`/browse?mode=alcoholic&value=${encodeURIComponent(alcoholic)}`, { replace: true });
  };

  const handleModeChange = (mode: BrowseMode) => {
    setBrowseMode(mode);
    setSelectedLetter('');
    setSelectedCategory('');
    setSelectedGlass('');
    setSelectedAlcoholic('');
    setCocktails([]);
  };

  return (
    <div className="browse">
      <h1>Browse Drinks</h1>

      {/* Browse Mode Tabs */}
      <div className="browse-tabs">
        <button
          className={`browse-tab ${browseMode === 'letter' ? 'active' : ''}`}
          onClick={() => handleModeChange('letter')}
        >
          By Letter
        </button>
        <button
          className={`browse-tab ${browseMode === 'category' ? 'active' : ''}`}
          onClick={() => handleModeChange('category')}
        >
          By Category
        </button>
        <button
          className={`browse-tab ${browseMode === 'glass' ? 'active' : ''}`}
          onClick={() => handleModeChange('glass')}
        >
          By Glass
        </button>
        <button
          className={`browse-tab ${browseMode === 'alcoholic' ? 'active' : ''}`}
          onClick={() => handleModeChange('alcoholic')}
        >
          By Type
        </button>
      </div>

      {/* Letter Browsing */}
      {browseMode === 'letter' && (
        <div className="letter-selector">
          <h2>Select a letter:</h2>
          <div className="letter-buttons">
            {letters.map(letter => (
              <button
                key={letter}
                onClick={() => handleLetterClick(letter)}
                className={`letter-btn ${selectedLetter === letter ? 'active' : ''}`}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category Browsing */}
      {browseMode === 'category' && (
        <div className="category-selector">
          <h2>Select a category:</h2>
          <div className="category-buttons">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => handleCategoryClick(category)}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Glass Browsing */}
      {browseMode === 'glass' && (
        <div className="glass-selector">
          <h2>Select a glass type:</h2>
          <div className="category-buttons">
            {glasses.map(glass => (
              <button
                key={glass}
                onClick={() => handleGlassClick(glass)}
                className={`category-btn ${selectedGlass === glass ? 'active' : ''}`}
              >
                {glass}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Alcoholic Type Browsing */}
      {browseMode === 'alcoholic' && (
        <div className="alcoholic-selector">
          <h2>Select by type:</h2>
          <div className="category-buttons">
            {alcoholicTypes.map(type => (
              <button
                key={type}
                onClick={() => handleAlcoholicClick(type)}
                className={`category-btn ${selectedAlcoholic === type ? 'active' : ''}`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="loading">Loading drinks...</div>
      )}

      {/* Results Display */}
      {((selectedLetter && browseMode === 'letter') ||
        (selectedCategory && browseMode === 'category') ||
        (selectedGlass && browseMode === 'glass') ||
        (selectedAlcoholic && browseMode === 'alcoholic')) &&
       !loading && cocktails.length > 0 && (
        <div className="browse-results">
          <h2>
            {browseMode === 'letter' && `Drinks starting with "${selectedLetter}" (${cocktails.length})`}
            {browseMode === 'category' && `${selectedCategory} Drinks (${cocktails.length})`}
            {browseMode === 'glass' && `Drinks served in ${selectedGlass} (${cocktails.length})`}
            {browseMode === 'alcoholic' && `${selectedAlcoholic} Drinks (${cocktails.length})`}
          </h2>
          <div className="cocktails-grid">
            {cocktails.map(cocktail => (
              <CocktailCard key={cocktail.idDrink} cocktail={cocktail} />
            ))}
          </div>
        </div>
      )}

      {((selectedLetter && browseMode === 'letter') ||
        (selectedCategory && browseMode === 'category') ||
        (selectedGlass && browseMode === 'glass') ||
        (selectedAlcoholic && browseMode === 'alcoholic')) &&
       !loading && cocktails.length === 0 && (
        <p className="no-results">
          {browseMode === 'letter' && `No drinks found starting with "${selectedLetter}"`}
          {browseMode === 'category' && `No drinks found in category "${selectedCategory}"`}
          {browseMode === 'glass' && `No drinks found served in ${selectedGlass}`}
          {browseMode === 'alcoholic' && `No ${selectedAlcoholic.toLowerCase()} drinks found`}
        </p>
      )}
    </div>
  );
};

export default Browse;
